<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';
    import { TRANSITION_DELAY } from '$lib/global/constants';

    import Transition from '$lib/components/Blocks/_wrappers/Transition.svelte';
    import { cn } from '$lib/utils/cn';

    import Answer from './Answer.svelte';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';
    import Button from '../Button/Button.View.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { Box as BoxType, Align } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { ComponentProps } from 'svelte';

    export let id = '';
    export let align: Align = 'center';
    export let wysiwygHtml: string | undefined = undefined;
    export let color = 'black';
    export let box: BoxType;
    export let answers: Omit<ComponentProps<Answer>, 'onClick' | 'onMouseEnter'>[] = [];

    export let onSubmit: (ids: string[]) => void;
    export let onMouseEnter: ComponentProps<Button>['onMouseEnter'];
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    export let btnId = '';
    export let btnColor = '#F0F0F0';
    export let btnBgColor = '#0076FA';
    export let btnAlign: Align = 'center';
    export let btnFullWidthOnDesktop = false;
    export let btnAnimation = '';
    export let btnWysiwygHtml: string | undefined = undefined;
    export let btnStyle: ButtonStyle = 'filled';
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;
    export let theme: Theme | undefined = undefined;

    const { isInColumn, isInSingleColumn } = layout;

    let selected: string[] = [];
    let error = false;

    const handleAnswerClick = (answerId: string) => {
        // see https://svelte.dev/tutorial/updating-arrays-and-objects
        if (selected.includes(answerId)) {
            selected = selected.filter((id) => id !== answerId);
        } else {
            selected = [...selected, answerId];
        }

        // Reset previously set error
        error = false;
    };

    const handleSubmit = () => {
        if (selected.length === 0) {
            error = true;

            return;
        }

        onSubmit(selected);
    };

    const textBox: BoxType = {
        top: 'none',
        right: 'none',
        bottom: 'large',
        left: 'none',
    };

    let isEven = answers.length % 2 === 0;

    const answersClasses = cn('answer fun-grid fun-grid-cols-1 fun-gap-4 sm:fun-mt-2', {
        'md:fun-grid-cols-2': answers.length % 2 === 0 && (!isInColumn || isInSingleColumn),
    });
</script>

<Box
    {id}
    {...getBoxAttributes({ box, isInColumn })}
    {transitionDelay}
    {transitionEffect}
    narrow={!isEven}
>
    <Text {wysiwygHtml} {align} {color} box={textBox} transitionEffect="none" />

    <div class={answersClasses}>
        {#each answers as answer, index (answer.id)}
            <Transition
                effect={transitionEffect}
                delay={transitionDelay + index * TRANSITION_DELAY}
            >
                <Answer {...answer} onClick={handleAnswerClick} />
            </Transition>
        {/each}
    </div>

    {#if error}
        <p class="fun-mt-4 fun-px-4 fun-text-center fun-text-sm fun-text-red-500">
            {language === 'de'
                ? 'Bitte wähle eine oder mehrere Antworten aus.'
                : 'Please choose one or more answers.'}
        </p>
    {/if}

    <div class="fun-mt-4">
        <Button
            id={btnId}
            wysiwygHtml={btnWysiwygHtml}
            color={btnColor}
            bgColor={btnBgColor}
            buttonType="submit"
            onClick={handleSubmit}
            align={btnAlign}
            fullWidthOnDesktop={btnFullWidthOnDesktop}
            box={DEFAULT_BOX}
            animation={btnAnimation}
            buttonStyle={btnStyle}
            {theme}
            {onMouseEnter}
            {transitionEffect}
            transitionDelay={transitionDelay + (answers.length + 1) * TRANSITION_DELAY}
        />
    </div>
</Box>

<style lang="scss">
    .answer :global(h1),
    .answer :global(h2),
    .answer :global(h3),
    .answer :global(h4),
    .answer :global(p) {
        padding: 0 !important;
    }
</style>
